/**
 * Brand Style Engine — the visual language of the design.
 *
 * A style carries the biases an art director applies before any content
 * exists: how loud the typography is, how much whitespace is non-negotiable,
 * how much depth (layering/shadows) fits, and the "feel" scores the brief
 * asked for (luxury / modern / minimal / corporate). `typographyTheme` names
 * the Template Factory typography theme that expresses the style.
 */

export const BRAND_STYLES = {
  luxury: {
    id: 'luxury', name: 'Luxury',
    typographyEmphasis: 0.55, whitespaceBias: 0.9, layerDepth: 0.3, decorationAffinity: 0.2,
    feel: { luxury: 1, modern: 0.4, minimal: 0.7, corporate: 0.3 },
    typographyTheme: 'elegant',
  },
  modern: {
    id: 'modern', name: 'Modern',
    typographyEmphasis: 0.7, whitespaceBias: 0.6, layerDepth: 0.5, decorationAffinity: 0.5,
    feel: { luxury: 0.3, modern: 1, minimal: 0.5, corporate: 0.5 },
    typographyTheme: 'modern',
  },
  corporate: {
    id: 'corporate', name: 'Corporate',
    typographyEmphasis: 0.5, whitespaceBias: 0.55, layerDepth: 0.25, decorationAffinity: 0.25,
    feel: { luxury: 0.25, modern: 0.6, minimal: 0.5, corporate: 1 },
    typographyTheme: 'modern',
  },
  creative: {
    id: 'creative', name: 'Creative',
    typographyEmphasis: 0.8, whitespaceBias: 0.45, layerDepth: 0.8, decorationAffinity: 0.9,
    feel: { luxury: 0.2, modern: 0.7, minimal: 0.2, corporate: 0.1 },
    typographyTheme: 'friendly',
  },
  minimal: {
    id: 'minimal', name: 'Minimal',
    typographyEmphasis: 0.45, whitespaceBias: 1, layerDepth: 0.1, decorationAffinity: 0.1,
    feel: { luxury: 0.6, modern: 0.7, minimal: 1, corporate: 0.5 },
    typographyTheme: 'modern',
  },
  bold: {
    id: 'bold', name: 'Bold',
    typographyEmphasis: 1, whitespaceBias: 0.35, layerDepth: 0.6, decorationAffinity: 0.6,
    feel: { luxury: 0.2, modern: 0.7, minimal: 0.2, corporate: 0.2 },
    typographyTheme: 'bold',
  },
  elegant: {
    id: 'elegant', name: 'Elegant',
    typographyEmphasis: 0.5, whitespaceBias: 0.85, layerDepth: 0.2, decorationAffinity: 0.3,
    feel: { luxury: 0.8, modern: 0.4, minimal: 0.6, corporate: 0.3 },
    typographyTheme: 'elegant',
  },
  street: {
    id: 'street', name: 'Street',
    typographyEmphasis: 0.95, whitespaceBias: 0.25, layerDepth: 0.85, decorationAffinity: 0.8,
    feel: { luxury: 0.1, modern: 0.6, minimal: 0.1, corporate: 0 },
    typographyTheme: 'bold',
  },
  editorial: {
    id: 'editorial', name: 'Editorial',
    typographyEmphasis: 0.75, whitespaceBias: 0.75, layerDepth: 0.35, decorationAffinity: 0.2,
    feel: { luxury: 0.6, modern: 0.7, minimal: 0.6, corporate: 0.4 },
    typographyTheme: 'elegant',
  },
  premium: {
    id: 'premium', name: 'Premium',
    typographyEmphasis: 0.6, whitespaceBias: 0.8, layerDepth: 0.4, decorationAffinity: 0.35,
    feel: { luxury: 0.9, modern: 0.6, minimal: 0.6, corporate: 0.4 },
    typographyTheme: 'elegant',
  },
};

export const listBrandStyles = () =>
  Object.values(BRAND_STYLES).map(({ id, name }) => ({ id, name }));

export const getBrandStyle = (id) => {
  const style = BRAND_STYLES[id];
  if (!style) throw new Error(`Unknown brand style "${id}"`);
  return style;
};
